import { UIHelpers } from './managers/ui-helpers'
import { SelectionPreviewRenderer } from './managers/selection-preview-renderer'
import { AppInitializer } from './managers/app-initializer'
import { StepRenderer } from './managers/step-renderer'

import { PDFViewer } from './components/pdf-viewer-legacy'
import { PlotViewer } from './components/plot-viewer'

export class App {
  private initializer: AppInitializer
  private managers: ReturnType<AppInitializer['getManagers']>
  private uiHelpers: UIHelpers
  private selectionPreviewRenderer: SelectionPreviewRenderer
  private stepRenderer: StepRenderer
  private pdfViewer: PDFViewer | null = null
  private plotViewer: PlotViewer | null = null

  constructor() {
    this.initializer = new AppInitializer()
    this.managers = this.initializer.getManagers()
    
    // Initialize helper classes
    this.uiHelpers = new UIHelpers(this.managers.apiService, this.managers.appState, this.managers.uiManager)
    this.selectionPreviewRenderer = new SelectionPreviewRenderer(this.managers.appState)
    this.stepRenderer = new StepRenderer(
      this.managers.apiService,
      this.managers.appState,
      this.managers.documentManager,
      this.managers.dataManager,
      this.managers.selectionManager,
      this.managers.modalManager,
      this.managers.textProcessingEventManager,
      this.managers.processingManager,
      this.selectionPreviewRenderer,
      this.managers.toast,
      this.managers.uiManager
    )

    // Setup global utilities and event listeners
    this.initializer.setAppInstance(this)
    this.initializer.setupGlobalUtilities()
    this.initializer.setupEventListeners()
    
    // Setup OCR processing event listener
    this.setupOCRProcessingEventListener()
  }

  public async init(): Promise<void> {
    // Use the existing initializer that was created in the constructor
    await this.initializer.init()
  }

  public async renderStepContent(): Promise<void> {
    console.log('🎯 App.renderStepContent() called')
    const stepContent = document.getElementById('step-content')
    if (!stepContent) {
      console.error('❌ step-content element not found!')
      return
    }

    const currentStep = this.managers.appState.getCurrentStep()
    const isAdvancedMode = this.managers.appState.getAdvancedMode()

    console.log('🔄 App.renderStepContent() - Current state:', {
      step: currentStep,
      isAdvancedMode,
      hasCoordinates: this.managers.appState.getCoordinates().length > 0,
      projectId: this.managers.appState.getCurrentProject()?.id,
      stepContentExists: !!stepContent
    })

    // Clean up components when navigating away from their steps
    if (this.pdfViewer) {
      console.log('🧹 Cleaning up PDF viewer before step change')
      this.pdfViewer.clearState()
      this.pdfViewer = null
    }

    if (currentStep !== 5 && this.plotViewer) {
      console.log('🧹 Cleaning up PlotViewer instance (navigating away from plotting step)')
      this.plotViewer = null
    }

    stepContent.innerHTML = ''

    // Ensure coordinates are restored when needed
    const currentProject = this.managers.appState.getCurrentProject()
    if (currentProject && ((currentStep === 4 && !isAdvancedMode) || (currentStep === 3 && isAdvancedMode)) && this.managers.appState.getCoordinates().length === 0) {
      console.log('🔄 No coordinates found in plotting step, attempting restore')
      this.managers.appState.restoreCoordinates(currentProject.id)
      console.log('🔄 After restore attempt, coordinates count:', this.managers.appState.getCoordinates().length)
    }

    // Delegate to StepRenderer
    console.log('🎯 Delegating to StepRenderer.renderStepContent()')
    await this.stepRenderer.renderStepContent()
    console.log('✅ StepRenderer.renderStepContent() completed')
  }

  // Delegate methods to appropriate managers
  public get apiService() { return this.managers.apiService }
  public get appState() { return this.managers.appState }
  public get authManager() { return this.managers.authManager }
  public get uiManager() { return this.managers.uiManager }
  public get textProcessingEventManager() { return this.managers.textProcessingEventManager }
  public get dataManager() { return this.managers.dataManager }
  public get documentManager() { return this.managers.documentManager }
  public get selectionManager() { return this.managers.selectionManager }
  public get processingManager() { return this.managers.processingManager }
  public get modalManager() { return this.managers.modalManager }
  public get ocrRenderer() { return this.managers.ocrRenderer }
  public get eventManager() { return this.managers.eventManager }
  public get toast() { return this.managers.toast }
  public get workflowNav() { return this.managers.workflowNav }

  // Delegate UI helper methods
  public renderConfidenceHighlightedText(text: string, wordData?: any[] | string, threshold: number = 75, highlightNumbers: boolean = false, highlightConfidence: boolean = true): string {
    return this.uiHelpers.renderConfidenceHighlightedText(text, wordData, threshold, highlightNumbers, highlightConfidence)
  }
  
  public autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    return this.uiHelpers.autoResizeTextarea(textarea)
  }
  
  public saveDescriptionListToDatabase(listText: string): Promise<void> {
    return this.uiHelpers.saveDescriptionListToDatabase(listText)
  }

  // Delegate selection preview rendering
  public renderSelectionPreview(selection: any): string {
    return this.selectionPreviewRenderer.renderSelectionPreview(selection)
  }

  // Delegate selection results rendering
  public renderSelectionResults(container: HTMLElement): void {
    return this.stepRenderer.renderSelectionResults(container)
  }

  // Setup OCR processing event listener
  private setupOCRProcessingEventListener(): void {
    window.addEventListener('initialize-ocr-processing', (event: any) => {
      console.log('🎯 initialize-ocr-processing event received')
      const selections = event.detail?.selections || []
      if (selections.length > 0) {
        console.log('🎯 Initializing OCR processing for', selections.length, 'selections')
        this.managers.ocrRenderer.initializeOCRProcessing(selections)
      } else {
        console.log('⚠️ No selections provided for OCR processing')
      }
    })
  }
}
